import * as THREE from 'three';

/** Cut height (metres) encoded by a texel value of 1; 1 also means "never cut". */
export const CUT_SCALE = 2.5;
/** Only the part of a stroke below this height above the ground cuts. */
const CUT_MAX = 1.1;
const SIZE = 1536;
const EXTENT = 56;
/** One 8-bit step of regrowth every REGROW_STEP seconds: ~25 s from the ground back to full height. */
const REGROW_STEP = 0.2;
const MAX_QUADS = 64;
const IDLE_RESET = 60;

const FULLSCREEN_VERTEX = /* glsl */ `
varying vec2 vUv;
void main() {
  vUv = position.xy * 0.5 + 0.5;
  gl_Position = vec4(position.xy, 0.0, 1.0);
}
`;

/**
 * World-anchored map of how low the grass has been cut, centred on the player and re-centred by
 * copying as the player travels. Strokes paint with MIN blending; regrowth adds one 8-bit step at a
 * time. The grass vertex shaders clamp blade height to the sampled value.
 */
export class GrassCut {
  readonly uniforms: { uCutTex: THREE.IUniform<THREE.Texture>; uCutInfo: THREE.IUniform<THREE.Vector4> };
  private readonly targets: THREE.WebGLRenderTarget[];
  private current = 0;
  private readonly origin = new THREE.Vector2();
  private readonly camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
  private readonly paintGeometry: THREE.BufferGeometry;
  private readonly paintPositions: Float32Array;
  private readonly paintCaps: Float32Array;
  private readonly paintMesh: THREE.Mesh;
  private readonly regrowMesh: THREE.Mesh;
  private readonly copyMesh: THREE.Mesh;
  private readonly copyMaterial: THREE.ShaderMaterial;
  private quads = 0;
  private regrowTimer = 0;
  private idle = IDLE_RESET;
  private needsClear = true;
  /** Strokes painted since load, for diagnostics and tests. */
  strokes = 0;

  constructor(private readonly renderer: THREE.WebGLRenderer) {
    const make = () =>
      new THREE.WebGLRenderTarget(SIZE, SIZE, {
        format: THREE.RedFormat,
        type: THREE.UnsignedByteType,
        minFilter: THREE.LinearFilter,
        magFilter: THREE.LinearFilter,
        generateMipmaps: false,
        depthBuffer: false,
        stencilBuffer: false,
      });
    this.targets = [make(), make()];
    this.uniforms = {
      uCutTex: { value: this.targets[0].texture },
      uCutInfo: { value: new THREE.Vector4(0, 0, 1 / EXTENT, 0) },
    };

    this.paintPositions = new Float32Array(MAX_QUADS * 6 * 3);
    this.paintCaps = new Float32Array(MAX_QUADS * 6);
    this.paintGeometry = new THREE.BufferGeometry();
    this.paintGeometry.setAttribute('position', new THREE.BufferAttribute(this.paintPositions, 3).setUsage(THREE.DynamicDrawUsage));
    this.paintGeometry.setAttribute('aCap', new THREE.BufferAttribute(this.paintCaps, 1).setUsage(THREE.DynamicDrawUsage));
    this.paintMesh = new THREE.Mesh(
      this.paintGeometry,
      new THREE.ShaderMaterial({
        uniforms: { uCutInfo: this.uniforms.uCutInfo },
        vertexShader: /* glsl */ `
          uniform vec4 uCutInfo;
          attribute float aCap;
          varying float vCap;
          void main() {
            vCap = aCap;
            gl_Position = vec4((position.xz - uCutInfo.xy) * uCutInfo.z * 2.0, 0.0, 1.0);
          }
        `,
        fragmentShader: /* glsl */ `
          varying float vCap;
          void main() {
            if (vCap > ${CUT_MAX.toFixed(2)}) discard;
            // The cut reads deeper than the edge passed: the wind off the blade shears the stalks low.
            gl_FragColor = vec4(max(vCap * 0.4 - 0.06, 0.06) / ${CUT_SCALE.toFixed(1)}, 0.0, 0.0, 1.0);
          }
        `,
        blending: THREE.CustomBlending,
        blendEquation: THREE.MinEquation,
        depthTest: false,
        depthWrite: false,
        side: THREE.DoubleSide,
      }),
    );
    this.paintMesh.frustumCulled = false;

    const fullscreen = new THREE.BufferGeometry();
    fullscreen.setAttribute('position', new THREE.Float32BufferAttribute([-1, -1, 0, 3, -1, 0, -1, 3, 0], 3));
    this.regrowMesh = new THREE.Mesh(
      fullscreen,
      new THREE.ShaderMaterial({
        vertexShader: FULLSCREEN_VERTEX,
        fragmentShader: /* glsl */ `void main() { gl_FragColor = vec4(1.0 / 255.0, 0.0, 0.0, 1.0); }`,
        blending: THREE.CustomBlending,
        blendEquation: THREE.AddEquation,
        blendSrc: THREE.OneFactor,
        blendDst: THREE.OneFactor,
        depthTest: false,
        depthWrite: false,
      }),
    );
    this.regrowMesh.frustumCulled = false;
    this.copyMaterial = new THREE.ShaderMaterial({
      uniforms: { uPrev: { value: null }, uShift: { value: new THREE.Vector2() } },
      vertexShader: FULLSCREEN_VERTEX,
      fragmentShader: /* glsl */ `
        uniform sampler2D uPrev;
        uniform vec2 uShift;
        varying vec2 vUv;
        void main() {
          vec2 uv = vUv + uShift;
          float inside = step(0.0, uv.x) * step(0.0, uv.y) * step(uv.x, 1.0) * step(uv.y, 1.0);
          gl_FragColor = vec4(mix(1.0, texture2D(uPrev, uv).r, inside), 0.0, 0.0, 1.0);
        }
      `,
      blending: THREE.NoBlending,
      depthTest: false,
      depthWrite: false,
    });
    this.copyMesh = new THREE.Mesh(fullscreen, this.copyMaterial);
    this.copyMesh.frustumCulled = false;
  }

  /**
   * Queue the surface swept by the blade between two frames. Heights are metres above the ground
   * at each corner; `groundAt` gives the terrain height.
   */
  stroke(b0: THREE.Vector3, t0: THREE.Vector3, b1: THREE.Vector3, t1: THREE.Vector3, groundAt: (x: number, z: number) => number): void {
    if (this.quads >= MAX_QUADS) return;
    const corners = [b0, t0, t1, b0, t1, b1];
    const base = this.quads * 6;
    for (let i = 0; i < 6; i++) {
      const p = corners[i];
      this.paintPositions.set([p.x, 0, p.z], (base + i) * 3);
      this.paintCaps[base + i] = p.y - groundAt(p.x, p.z);
    }
    this.quads++;
  }

  /** Flush queued strokes, regrow, and keep the map centred on the player. Call once per frame. */
  update(dt: number, player: THREE.Vector3): void {
    const info = this.uniforms.uCutInfo.value;
    const texel = EXTENT / SIZE;
    const active = this.idle < IDLE_RESET || this.quads > 0;
    if (Math.hypot(player.x - this.origin.x, player.z - this.origin.y) > EXTENT * 0.22 || this.needsClear) {
      const nx = Math.round(player.x / texel) * texel;
      const nz = Math.round(player.z / texel) * texel;
      if (active && !this.needsClear) {
        this.copyMaterial.uniforms.uPrev.value = this.targets[this.current].texture;
        this.copyMaterial.uniforms.uShift.value.set((nx - this.origin.x) / EXTENT, (nz - this.origin.y) / EXTENT);
        this.current = 1 - this.current;
        this.draw(this.copyMesh);
      }
      this.origin.set(nx, nz);
      info.x = nx;
      info.y = nz;
    }
    if (this.needsClear) this.clear();

    if (this.quads > 0) {
      this.paintGeometry.setDrawRange(0, this.quads * 6);
      this.paintGeometry.attributes.position.needsUpdate = true;
      this.paintGeometry.attributes.aCap.needsUpdate = true;
      this.draw(this.paintMesh);
      this.quads = 0;
      this.idle = 0;
      this.strokes++;
    } else if (this.idle < IDLE_RESET) {
      this.idle += dt;
      this.regrowTimer += dt;
      if (this.regrowTimer >= REGROW_STEP) {
        this.regrowTimer = 0;
        this.draw(this.regrowMesh);
      }
      if (this.idle >= IDLE_RESET) this.needsClear = true;
    }
    info.w = this.idle < IDLE_RESET ? 1 : 0;
    this.uniforms.uCutTex.value = this.targets[this.current].texture;
  }

  private clear(): void {
    this.needsClear = false;
    const r = this.renderer;
    const prevTarget = r.getRenderTarget();
    const prevColor = r.getClearColor(new THREE.Color());
    const prevAlpha = r.getClearAlpha();
    r.setClearColor(0xffffff, 1);
    for (const t of this.targets) {
      r.setRenderTarget(t);
      r.clear(true, false, false);
    }
    r.setRenderTarget(prevTarget);
    r.setClearColor(prevColor, prevAlpha);
  }

  private draw(mesh: THREE.Mesh): void {
    const r = this.renderer;
    const prevTarget = r.getRenderTarget();
    const prevAutoClear = r.autoClear;
    r.autoClear = false;
    r.setRenderTarget(this.targets[this.current]);
    r.render(mesh, this.camera);
    r.setRenderTarget(prevTarget);
    r.autoClear = prevAutoClear;
  }

  dispose(): void {
    for (const t of this.targets) t.dispose();
  }
}
